"use strict"

import React, { useState, useEffect } from "react"
import { Button, ButtonGroup } from "react-bootstrap"
import _ from "lodash"

import ObservationSelectorPopper from "../graphing/plotting/widgets/ObservationSelectorPopper"
import TagDefinitionsPopper from "../graphing/plotting/widgets/TagDefinitionsPopper"
import { isNumber } from "../utils"
import { isLTPM } from "../../components/shared_components_registration"
import DateInputField from "../widgets/DateInputField"
import ToggleEventLayers from "../graphing/plotting/widgets/ToggleRunLabelsButton/ToggleEventLayers"
import ReactSelectionElement from "../widgets/ReactSelectionElement"

const registration = {
  numerical_warn: (intl) =>
    isLTPM
      ? intl.formatMessage({
          id: "general.please.enter.number",
        })
      : "Please enter valid number",
  date_warn: (intl) =>
    isLTPM
      ? intl.formatMessage({
          id: "general.please.enter.date",
        })
      : "Please enter valid date",
  select_tag: (intl) => (isLTPM ? intl.formatMessage({ id: "general.select" }) : "Select"),
}

export default function RunFilterEditorContainer({ onUpdate, onDelete, tagList, filter, prevFilter, intl }) {
  const { tag, units, relation, valueString } = filter
  const relations = [
    {
      name: "gte",
      label: "\u2265",
    },
    {
      name: "lte",
      label: "\u2264",
    },
  ]

  const [tagDesc, setTagDesc] = useState({})
  const {
    isNumerical,
    isDate,
    isText,
    isDropdown,
    isTag,
    title,
    options = [],
    units_options = [],
    type_options = [],
  } = tagDesc
  useEffect(() => {
    setTagDesc((tag && tagList.find((t) => t.tag === tag)) || {})
  }, [tagList])
  const [tagWarningMsg, setTagWarningMsg] = useState(null)
  const isFilterChanged = !_.isEqual(filter, prevFilter)

  useEffect(() => {
    if (isNumerical && !isNumber(valueString)) {
      setTagWarningMsg(registration.numerical_warn(intl))
    } else if (isDate && !valueString) {
      setTagWarningMsg(registration.date_warn(intl))
    } else {
      setTagWarningMsg(null)
    }
  }, [valueString, tagDesc])

  return (
    <div>
      <ButtonGroup
        style={
          isFilterChanged
            ? { alignItems: "center", border: "solid var(--lanzalake-red) 3px", borderRadius: "7px" }
            : { alignItems: "center" }
        }
      >
        <ObservationSelectorPopper
          placement={"right"}
          options={tagList}
          nameFn={(plot) => plot.title}
          onSelect={(sel) => {
            let info = { tag: sel.tag, category: sel.category, valueString: null, isTag: sel.isTag }
            if (sel.isJsonField) {
              info = {
                ...info,
                isJsonField: sel.isJsonField,
                jsonBase: sel.jsonBase,
              }
            } else if (sel.tag === "run_length") {
              info = {
                ...info,
                relation: relations[0]["name"],
                units: sel.units_options && sel.units_options[0],
              }
            } else if (sel.isDate) {
              info = { ...info, relation: relations[0]["name"], isDate: sel.isDate }
            } else if (sel.isDropdown) {
              info = {
                ...info,
                valueString: sel.valueString ? sel.valueString : sel.options && sel.options[0],
                isDropdown: sel.isDropdown,
              }
            }
            onUpdate(info)
            setTagWarningMsg(null)
          }}
        >
          <Button variant={"outline-primary"}>
            {title || tag || <span>{registration.select_tag(intl)}</span>}
          </Button>
        </ObservationSelectorPopper>

        {relation && (
          <ReactSelectionElement
            name={"relation"}
            klass={""}
            list={relations}
            getOptionLabel={(obj) => obj.label}
            getOptionValue={(obj) => obj.name}
            value={relations.find((r) => r.name === relation) || { name: "", label: "" }}
            onValueChange={(_, selected) => {
              onUpdate({
                relation: selected?.name,
              })
            }}
            isMulti={false}
            isClearable={false}
          />
        )}

        {isNumerical ? (
          <input
            type={"number"}
            className={"form-control"}
            value={valueString || ""}
            style={{
              color: !isNumber(valueString) ? "var(--lanzalake-red)" : "var(--lanzalake-black)",
              maxWidth: "125px",
            }}
            onChange={(ev) =>
              isNumber(ev.target.value) &&
              onUpdate({
                valueString: ev.target.value,
              })
            }
          />
        ) : isText ? (
          <input
            type={"text"}
            className={"form-control"}
            value={valueString || ""}
            style={{
              width: "300px",
            }}
            onChange={(ev) =>
              onUpdate({
                valueString: ev.target.value,
              })
            }
          />
        ) : isDate ? (
          <DateInputField value={valueString} valChange={(val) => onUpdate({ valueString: val })} />
        ) : isDropdown ? (
          <ReactSelectionElement
            name={"options"}
            klass={""}
            disabled={options.length === 0}
            list={options.map((o) => {
              return { value: o }
            })}
            value={{ value: valueString || "" }}
            onValueChange={(_, selected) => {
              onUpdate({
                valueString: selected?.value,
              })
            }}
            isMulti={false}
            isClearable={false}
          />
        ) : null}

        {tag && units_options.length > 0 && !isTag && (
          <ReactSelectionElement
            name={"units"}
            klass={""}
            list={units_options.map((o) => {
              return { value: o }
            })}
            value={{ value: units || "" }}
            onValueChange={(_, selected) => {
              onUpdate({
                units: selected?.value,
              })
            }}
            isMulti={false}
            isClearable={false}
          />
        )}

        <Button variant={"outline-primary"} onClick={onDelete}>
          <span className={"fa fa-minus-circle"} />
        </Button>

        {isTag && type_options.includes("Computed") && (
          <span style={{ paddingLeft: "3px" }}>
            <TagDefinitionsPopper tag={tag} tagData={tagDesc} />
          </span>
        )}

        {!isLTPM && tag === "event_labels" && (
          <ToggleEventLayers
            small={false}
            canToggleVisibility={false}
            selectEventLabel={(event) =>
              onUpdate({
                valueString: event?.name || "",
              })
            }
          />
        )}

        {/* Messages To User */}
        {tagWarningMsg && (
          <span
            className={"required-message"}
            style={{
              paddingLeft: "10px",
              paddingRight: "10px",
            }}
          >
            {`* ${tagWarningMsg}`}
          </span>
        )}
        {/* END Messages To User */}
      </ButtonGroup>
    </div>
  )
}
